import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RegisteredRetailer } from 'src/app/models/registered-retailer.model';
import { RetailerService } from 'src/app/service/retailer.service';
import { RetailerDataStoreService } from 'src/app/service/retailer-data-store.service';
import { toRetailer } from 'src/app/models/add-retailer.model';
import { HttpErrorResponse } from '@angular/common/http';
import { GeneratePresignedUrlModel, GeneratePresignedUrlResModel } from 'src/app/models/generate-presigned-url.model';
import { Location } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { NetworkService } from 'src/app/service/network.service';

@Component({
  selector: 'app-shop-details',
  templateUrl: './shop-details.component.html',
  styleUrls: ['./shop-details.component.scss']
})
export class ShopDetailsComponent implements OnInit, OnDestroy {
  formData: RegisteredRetailer | null = null;
  isLoading: boolean = false;
  isEditFlow: boolean = false;
  validationErrors: { [key: string]: string } = {};
  private destroy$ = new Subject<void>(); 
  isDataSaved: boolean = false;
  private networkStatusSubscription: Subscription | undefined;
  isOnline: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private retailerService: RetailerService,
    private retailerDataStore: RetailerDataStoreService,
    private location: Location,
    private networkService: NetworkService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isEditFlow = params['isEditFlow'] === 'true';
      this.formData = this.retailerDataStore.getRetailer();

      if (!this.formData) {
        this.snackBar.open('Retailer data missing for shop details. Please start from the beginning.', 'Dismiss', { duration: 5000 });
        this.router.navigate(['/dealers'], { replaceUrl: true });
      }
      console.log('ShopDetailsComponent initialized with formData:', this.formData, 'isEditFlow:', this.isEditFlow);
    });
    this.networkStatusSubscription = this.networkService.networkStatus$.subscribe(isOnline => {
      this.isOnline = isOnline;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.networkStatusSubscription?.unsubscribe();    
  }

  onInputChange(fieldName: string): void {
    if (this.validationErrors[fieldName]) {
      delete this.validationErrors[fieldName];
    }
  }

  goBack(): void {
    this.router.navigate(['/dealers'], { replaceUrl: true });
  }

  async onSubmitDetails(): Promise<void> {
    if (!this.formData) {
      this.snackBar.open('No data to submit.', 'Dismiss', { duration: 3000 });
      return;
    }

    this.validationErrors = {};
    if (!this.formData.mobileNumberPrimary) {
      this.validationErrors['mobileNumberPrimary'] = 'Primary mobile number is required.';
    }
    if (!this.formData.type) {
      this.validationErrors['type'] = 'Shop type is required.';
    }
    if (!this.formData.shopName) {
      this.validationErrors['shopName'] = 'Shop name is required.';
    }
    if (!this.formData.state) {
      this.validationErrors['state'] = 'County is required.';
    }
    if (!this.formData.district) {
      this.validationErrors['district'] = 'Sub county is required.';
    }
    if (!this.formData.village) {
      this.validationErrors['village'] = 'Ward is required.';
    }

    if (Object.keys(this.validationErrors).length > 0) {
      this.snackBar.open('Please fill all mandatory fields and correct errors.', 'Dismiss', { duration: 5000 });
      return;
    }

    this.isLoading = true;

    try {
      const capturedImageFile = this.retailerDataStore.getCapturedImageFile();

      if (capturedImageFile) {
        const objectKey = await this.uploadImageToS3(capturedImageFile);
        if (objectKey) {
          this.formData.retailerShopImage = objectKey;
          this.retailerDataStore.setRetailer(this.formData);
        } else {
          this.snackBar.open('Failed to upload image. Please try again.', 'Dismiss', { duration: 5000 });
          this.isLoading = false;
          return;
        }
      } else if (!this.formData.retailerShopImage) {
          this.snackBar.open('Please capture a photo of the shop from the previous step.', 'Dismiss', { duration: 5000 });
          this.isLoading = false;
          return;
      }

      this.retailerService.createOrUpdateRetailer(toRetailer(this.formData)).subscribe(
        response => {
          this.isLoading = false;
          if (response.metaData.responseCode === '200 OK' || response.metaData.responseCode === '201 CREATED') {
            this.retailerDataStore.clearRetailer();
            this.retailerDataStore.clearCapturedImageFile();

            this.isDataSaved = true;
            this.router.navigate(['/success'], {replaceUrl: true});
          } else {
            this.snackBar.open('Failed to save Agro Dealer details.', 'Dismiss', { duration: 5000 });
          }
        },
        (error: HttpErrorResponse) => {
          this.isLoading = false;
          if (error.status === 400 && error.error && error.error.data) {
            const errorDataString = error.error.data;
            console.error('Backend validation errors:', errorDataString);
            const rawErrors = errorDataString.replace('[', '').replace(']', '').split(',');

            this.validationErrors = {};

            rawErrors.forEach((errSegment: string) => {
              const trimmedSegment = errSegment.trim();
              const firstSpaceIndex = trimmedSegment.indexOf(' ');
              if (firstSpaceIndex !== -1) {
                let fieldName = trimmedSegment.substring(0, firstSpaceIndex).trim();
                let errorMessage = trimmedSegment.substring(firstSpaceIndex + 1).trim();

                this.validationErrors[fieldName] = errorMessage;
              }
            });
            this.snackBar.open('Please correct the highlighted errors.', 'Dismiss', { duration: 5000 });
          } else {
            this.snackBar.open('Error saving Agro Dealer details. Please try again.', 'Dismiss', { duration: 5000 });
            console.error('Save Retailer Error:', error);
          }
        }
      );
    } catch (error) {
      this.isLoading = false;
      this.snackBar.open('An unexpected error occurred during image upload or submission.', 'Dismiss', { duration: 5000 });
      console.error('Submission Process Error:', error);
    }
  }

  private async uploadImageToS3(file: File): Promise<string | null> {
    const fileExtension = file.name.split('.').pop() || '';
    const referenceName = 'RETAILER_IMAGE';

    const payload: GeneratePresignedUrlModel = {
      fileExtention: fileExtension,
      referenceName: referenceName,
      count: 1
    };

    try {
      const presignedUrlResponse = await this.retailerService.getPresignedUrl(payload).toPromise();

      if (presignedUrlResponse && presignedUrlResponse.data && presignedUrlResponse.data.length > 0) {
        const presignedUrlInfo: GeneratePresignedUrlResModel = presignedUrlResponse.data[0];
        const s3UploadUrl = presignedUrlInfo.url;
        const objectKey = presignedUrlInfo.objectKey;

        await this.retailerService.uploadFileToS3(s3UploadUrl, file).toPromise();

        return objectKey;
      } else {
        console.error('Failed to get presigned URL:', presignedUrlResponse);
        return null;
      }
    } catch (error) {
      console.error('Error during presigned URL generation or S3 upload:', error);
      return null;
    }
  }
}